import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Wrench } from 'lucide-react';
import { Logo } from './Logo';
import { CONTACT } from '../lyphline';

const SERVICES = [
  { label: 'Residential Machines', to: '/machines/residential' },
  { label: 'Small Business Machines', to: '/machines/small-business' },
  { label: 'Semi Commercial Machines', to: '/machines/semi-commercial' },
  { label: 'Diagnostics & Assessment', to: '/#services' },
  { label: 'Rebuilds & Maintenance', to: '/#rebuilds' },
  { label: 'Modifications', to: '/#retrofits' },
  { label: 'Parts & Components', to: '/process-rates' },
  { label: 'Downloads & Guides', to: '/documents' },
  { label: 'Troubleshooting Guides', to: '/guides' },
  { label: 'Freeze Dry Academy', to: '/freeze-dry-academy' },
  { label: 'Financing Support', to: '/financing' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#003456]/95 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
            <Logo size="lg" />
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-home">Home</Link>

            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-services">
                Services <ChevronDown className="h-4 w-4" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                <div className="w-64 bg-[#023F63] rounded-xl shadow-2xl border border-white/10 py-2">
                  {SERVICES.map((s) => (
                    <Link key={s.label} to={s.to} className="block px-4 py-2.5 text-sm text-white/75 hover:text-[#CE8946] hover:bg-white/5 transition-colors">
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/process-rates" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-process-rates">Process &amp; Rates</Link>
            <Link to="/technical-support" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-support">Technical Support</Link>
            <Link to="/documents" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-resources">Resources</Link>
            <Link to="/about" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-about">Our Story</Link>
            <Link to="/contact" className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="nav-contact">Contact</Link>
          </div>

          {/* Right: phone + CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-sm font-semibold text-white hover:text-[#CE8946] transition-colors" data-testid="nav-phone">
              <Phone className="h-4 w-4 text-[#CE8946]" /> {CONTACT.phone}
            </a>
            <Link
              to="/repair/quote"
              className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold text-sm px-5 py-2.5 rounded-md transition-colors shadow-lg shadow-[#CE8946]/20"
              data-testid="nav-diagnostic-quote"
            >
              <Wrench className="h-4 w-4" /> Free Diagnostic Quote
            </Link>
          </div>

          {/* Mobile toggle */}
          <button className="lg:hidden p-2 text-white" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-[#003456] border-t border-white/10 px-6 py-5">
          <div className="flex flex-col gap-1">
            <Link to="/" className="py-2.5 text-white font-medium" onClick={() => setIsOpen(false)}>Home</Link>
            <p className="pt-3 pb-1 text-xs uppercase tracking-widest text-white/40">Services</p>
            {SERVICES.map((s) => (
              <Link key={s.label} to={s.to} className="py-2 pl-3 text-white/75" onClick={() => setIsOpen(false)}>{s.label}</Link>
            ))}
            <Link to="/process-rates" className="py-2.5 mt-2 text-white font-medium" onClick={() => setIsOpen(false)}>Process &amp; Rates</Link>
            <Link to="/technical-support" className="py-2.5 text-white font-medium" onClick={() => setIsOpen(false)} data-testid="mobile-nav-support">Technical Support</Link>
            <Link to="/documents" className="py-2.5 text-white font-medium" onClick={() => setIsOpen(false)} data-testid="mobile-nav-resources">Resources</Link>
            <Link to="/about" className="py-2.5 text-white font-medium" onClick={() => setIsOpen(false)}>Our Story</Link>
            <Link to="/contact" className="py-2.5 text-white font-medium" onClick={() => setIsOpen(false)}>Contact</Link>
            <a href={CONTACT.phoneHref} className="flex items-center gap-2 py-2.5 text-[#CE8946] font-semibold">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <Link
              to="/repair/quote"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#CE8946] text-[#003456] font-bold px-5 py-3 rounded-md"
              onClick={() => setIsOpen(false)}
            >
              <Wrench className="h-4 w-4" /> Free Diagnostic Quote
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
